import { useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { Logo } from './Logo'
import { Button } from '../ui/Button'
import { useAuth } from './providers/AuthProvider'
import { useReports } from '../api/queries/reports'
import { FeedbackDialog } from '../features/settings/FeedbackDialog'
import { BillingReturnNotice } from '../features/billing/BillingReturnNotice'
import styles from './AppShell.module.css'

/**
 * The shell. Only the destinations that exist are listed — an empty route
 * advertising a screen we have not built is the fake-control rule applied to
 * navigation (docs/16 section 6.5).
 *
 * Reports is listed only once the account has enough analysed calls for it to
 * hold anything (Mike, 2026-08-13: hidden until then). The route still exists
 * and explains itself if someone arrives by URL — hiding the link is not the
 * same as pretending the screen is unreachable.
 */
export function AppShell() {
  const { signOut } = useAuth()
  const reports = useReports()
  const [feedbackOpen, setFeedbackOpen] = useState(false)

  return (
    <div className={styles.app}>
      <nav className={styles.nav} aria-label="Main">
        <div className={styles.navLogo}>
          <Logo />
        </div>
        <ul className={styles.navList}>
          <li>
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                isActive ? `${styles.navLink} ${styles.navLinkActive}` : styles.navLink
              }
            >
              Pipeline
            </NavLink>
          </li>
          {reports.data?.available ? (
            <li>
              <NavLink
                to="/reports"
                className={({ isActive }) =>
                  isActive ? `${styles.navLink} ${styles.navLinkActive}` : styles.navLink
                }
              >
                Reports
              </NavLink>
            </li>
          ) : null}
          <li>
            <NavLink
              to="/settings"
              className={({ isActive }) =>
                isActive ? `${styles.navLink} ${styles.navLinkActive}` : styles.navLink
              }
            >
              Settings
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/help"
              className={({ isActive }) =>
                isActive ? `${styles.navLink} ${styles.navLinkActive}` : styles.navLink
              }
            >
              Help Centre
            </NavLink>
          </li>
        </ul>
        <div className={styles.navBottom}>
          <Button onClick={() => setFeedbackOpen(true)}>Send feedback</Button>
          <Button onClick={signOut}>Sign out</Button>
        </div>
      </nav>
      <div className={styles.main}>
        <BillingReturnNotice />
        <Outlet />
      </div>
      {feedbackOpen ? <FeedbackDialog onClose={() => setFeedbackOpen(false)} /> : null}
    </div>
  )
}

export const shellStyles = styles
